import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { defer, Observable, of } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';

import {
  clearSsnFormat,
  numericOnly as externalNumericOnly,
  formatDate,
  getErrorMessage,
  markFormAsUntouched,
} from 'src/app/util';
import { AppConstants } from '../../../app.constants';
import {
  MemberDetails,
  MemberSearchRequest,
  SaveAuditLogsRequestInterface,
  ClientInfoResponse,
  ILocationCodes,
  Coverage,
} from '@interfaces';
import { AppInsightsService, AppCenterAnalytics } from '@services';
import { LoaderService, AddMemberResponseDialogComponent } from '@management';
import { MemberModificationErrorHandlerComponent } from '../../../core/components/error/member-modification-error-handler/member-modification-error-handler.component';
import {
  AuthService,
  AuditLogsService,
  ErrorManagerService,
  ErrorType,
  EligibilityService,
} from '@core';
import { dateValidator } from '@shared';
import { ApplyCardholderFutureCoverageDialog } from './apply-cardholder-future-coverage-dialog-component/apply-cardholder-future-coverage-dialog.component';

@Component({
  selector: 'app-add-dependent',
  templateUrl: './add-dependent.component.html',
  styleUrls: ['./add-dependent.component.scss'],
  standalone: false,
})
export class AddDependentComponent implements OnInit {
  @ViewChild('addDependentContainer') addDependentContainer!: ElementRef;
  @ViewChild('stepper') stepper!: MatStepper;
  public isLinear = true;
  public personalIfoFormGroup: FormGroup;
  public checkTouchForPersonalInfo = false;
  public contactInfoFormGroup: FormGroup;
  public phoneTypes: { value: string; name: string }[] = [
    { value: '', name: 'Select Phone Type' },
    { value: 'homePhone', name: 'Home Phone' },
    { value: 'cellPhone', name: 'Cell Phone' },
    { value: 'workPhone', name: 'Work Phone' },
  ];
  public checkTouchForContactInfo = false;
  public maxDate = new Date();
  public genderList: { key: string; name: string }[] = AppConstants.GENDER_LIST;
  public personCodes: string[] = ['03', '04', '05'];
  public relationshipData: { name: string; value: string }[] =
    [...AppConstants.RELATIONSHIP_CODE];
  public selectedMemberDetails$: Observable<Partial<MemberDetails>>;
  public memberDetails: MemberDetails;
  public familyDetails: MemberDetails[];
  public displayGender = AppConstants.DISPLAY_GENDER;
  public displayCoverageType = AppConstants.DISPLAY_COVERAGE_TYPE;
  public relationShip = AppConstants.RELATIONSHIP_VALUES;
  public effectiveDateMaxDate = new Date();
  public effectiveDateMinDate = new Date();
  public isExpanded = false;

  public displayedColumns: string[] = [
    'personCode',
    'name',
    'cardId',
    'relationshipCode',
    'gender',
    'DOB',
    'startDate',
    'endDate',
  ];
  dataSource!: MatTableDataSource<MemberDetails>;
  dependents$!: Observable<MemberDetails[]>;
  public totalDependents: number;
  numericOnly = externalNumericOnly;
  newDependent: Partial<MemberDetails> = {};
  public cardHolderFutureCoverage: Coverage;

  groupInfo: { groupId: string; groupName: string } = {
    groupId: '',
    groupName: '',
  };
  clientInfo$: Observable<ClientInfoResponse[]>;
  locationCode: ILocationCodes[];
  public isSaving = false;
  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private readonly appInsightService: AppInsightsService,
    private readonly eligibilityService: EligibilityService,
    private activatedRoute: ActivatedRoute,
    private loader: LoaderService,
    private matDialog: MatDialog,
    private auditLogsService: AuditLogsService,
    private authService: AuthService,
    private errorManager: ErrorManagerService,
    private cdr: ChangeDetectorRef
  ) { }
  ngOnInit(): void {
    const requiredValidator = Validators.required;
    const emailValidator = Validators.pattern(
      AppConstants.EMAIL_VALIDATION_PATTERN
    );
    this.setupPersonalInfoFormGroup(requiredValidator);
    this.selectedMemberDetails$ = defer(() => {
      this.loader.showLoader();
      return this.activatedRoute.queryParams.pipe(
        switchMap((params) => {
          if (params.refreshPage) {
            if (this.stepper) {
              this.stepper.selectedIndex = 0;
            }
            this.loader.showLoader();
            this.personalIfoFormGroup.reset();
            this.contactInfoFormGroup.reset();
            markFormAsUntouched(this.personalIfoFormGroup);
            markFormAsUntouched(this.contactInfoFormGroup);
            this.checkTouchForPersonalInfo = false;
            this.personalIfoFormGroup.controls.coverageType.setValue('FAM');
          }
          const details = {
            clientId: params.clientId,
            cardId: params.cardId,
          };
          const query = this.eligibilityService.getSearchQuery(details);
          return this.eligibilityService.getMemberDetails(query).pipe(
            switchMap((res) => {
              this.resetScroll();
              this.isSaving = false;
              this.memberDetails = this.filterOutCardHolderDetails(res);
              this.effectiveDateMinDate = new Date(this.memberDetails.coverage.startDate);
              this.setupValidations(res, requiredValidator);
              this.getClientInfo();
              this.getDependentsInfo();

              return this.getCardHolderFutureCoverageDetails(params, res);
            })
          );
        })
      );
    });
    this.appInsightService.trackPageView(AppCenterAnalytics.ADD_DEPENDENT);

    this.setupClientInfoFormGroup(emailValidator);
  }

  private setupValidations(res: MemberDetails[], requiredValidator: (control: AbstractControl) => ValidationErrors | null) {
    if (new Date(this.effectiveDateMinDate).valueOf() > new Date().valueOf()) {
      this.effectiveDateMaxDate = new Date(this.memberDetails.coverage.endDate);
    }
    this.familyDetails = res;
    if (this.memberDetails.clientInternalMemberId) {
      this.personalIfoFormGroup
        .get('clientInternalMemberId')
        .setValidators(requiredValidator);
    }
  }

  private getCardHolderFutureCoverageDetails(params: Params, res: MemberDetails[]): Observable<MemberDetails> {
    return this.eligibilityService
      .getCoverageDetails(
        params.clientId,
        params.cardId,
        this.memberDetails.personCode ?? '',
        this.memberDetails.empirxMemberId ?? ''
      )
      .pipe(
        tap((coverageRes) => {
          this.cardHolderFutureCoverage =
            coverageRes.coverage.find(c => c.eligibilityIndicator === 'F');
        }),
        map(() => res[0]),
        finalize(() => {
          this.loader.hideLoader();
        })
      );
  }

  private getDependentsInfo(): void {
    this.dependents$ = this.getDependents().pipe(
      tap((res) => {
        this.assignRelationShipValues(res);
        this.setDataSource(res);
        this.totalDependents = res.length;
        this.cdr.detectChanges();
      })
    );
  }

  private getClientInfo(): void {
    this.clientInfo$ = this.eligibilityService
      .getClientDetailsForBenefit(this.memberDetails.clientId)
      .pipe(
        tap((res) => {
          this.groupInfo = this.eligibilityService.extractGroupInfo(
            res,
            this.memberDetails.coverage.groupNumber
          );
          this.getLocationCodes(res);
        })
      );
  }

  private setupPersonalInfoFormGroup(requiredValidator): void {
    this.personalIfoFormGroup = this.formBuilder.group({
      firstName: [
        '',
        [
          requiredValidator,
          Validators.pattern(AppConstants.NAME_VALIDATION_PATTERN),
          Validators.maxLength(35),
        ],
      ],
      middleName: [
        '',
        [
          Validators.maxLength(35),
          Validators.pattern(AppConstants.NAME_VALIDATION_PATTERN),
        ],
      ],
      lastName: [
        '',
        [
          requiredValidator,
          Validators.pattern(AppConstants.NAME_VALIDATION_PATTERN),
          Validators.maxLength(35),
        ],
      ],
      gender: ['', requiredValidator],
      dob: ['', [requiredValidator, dateValidator()]],
      ssn: [
        '',
        [
          Validators.maxLength(9),
          Validators.minLength(9),
          Validators.pattern(/^\d{1,9}$/),
        ],
      ],
      clientInternalMemberId: ['', [Validators.maxLength(9)]],
      effectiveDate: ['', [requiredValidator, dateValidator()]],
      relationShip: ['', requiredValidator],
      coverageType: ['FAM', requiredValidator],
    });
  }

  private setupClientInfoFormGroup(emailValidator): void {
    this.contactInfoFormGroup = this.formBuilder.group({
      phoneType: [''],
      phoneNumber: [
        { value: '', disabled: true },
        [Validators.minLength(10), Validators.maxLength(10)],
      ],
      email: ['', [emailValidator]],
    });
    this.contactInfoFormGroup.controls.phoneType.valueChanges.subscribe(
      (value) => {
        if (value == null || value == '') {
          this.contactInfoFormGroup.get('phoneNumber')?.disable();
          this.contactInfoFormGroup.get('phoneNumber').setValidators(null);
          this.contactInfoFormGroup.controls.phoneNumber.setValue('');
          this.contactInfoFormGroup.get('phoneNumber').updateValueAndValidity();
        } else {
          this.contactInfoFormGroup.get('phoneNumber')?.enable();
          this.contactInfoFormGroup
            .get('phoneNumber')
            .setValidators([
              Validators.required,
              Validators.maxLength(10),
              Validators.minLength(10),
            ]);
          this.contactInfoFormGroup.get('phoneNumber').updateValueAndValidity();
        }
      }
    );
  }

  private setDataSource(res: MemberDetails[]): void {
    const softDeletedUsers = res.filter((m) => m.isSoftDeleted).sort((a, b) => parseInt(a.personCode) - parseInt(b.personCode));
    const nonSoftDeletedUsers = res.filter((m) => !m.isSoftDeleted).sort((a, b) => parseInt(a.personCode) - parseInt(b.personCode));
    const termedUsers = nonSoftDeletedUsers
      .filter(
        (m) => new Date(m.coverage.endDate).valueOf() <
          new Date(new Date().setUTCHours(0, 0, 0, 0)).valueOf()
      )
      .sort(
        (a, b) => parseInt(a.personCode) - parseInt(b.personCode)
      );
    const activeUsers = nonSoftDeletedUsers
      .filter(
        (m) => new Date(m.coverage.endDate).valueOf() >=
          new Date(new Date().setUTCHours(0, 0, 0, 0)).valueOf()
      )
      .sort(
        (a, b) => parseInt(a.personCode) - parseInt(b.personCode)
      );
    this.dataSource = new MatTableDataSource([
      ...activeUsers,
      ...termedUsers,
      ...softDeletedUsers
    ]);
  }

  private assignRelationShipValues(res: MemberDetails[]): void {
    res.forEach(
      (res) => (res.relationshipValue =
        AppConstants.RELATIONSHIP_VALUES[res.relationshipCode])
    );
  }

  private getLocationCodes(res: ClientInfoResponse[]): void {
    for (const detail of res) {
      for (const plan of detail.plans) {
        for (const group of plan.groups) {
          if (group.groupId.toLowerCase() ===
            this.memberDetails.coverage.groupNumber.toLowerCase()) {
            this.locationCode = group.locationCodes;
            break;
          }
        }
      }
    }
  }

  public filterOutMemberDetails(familyDetails: MemberDetails[], personCode: string): MemberDetails {
    return familyDetails.filter(
      (i) =>
        i.personCode ===
        personCode
    )[0];
  }

  public filterOutCardHolderDetails(familyDetails: MemberDetails[]): MemberDetails {
    return familyDetails.filter(
      (i) =>
        i.relationshipCode ===
        AppConstants.RELATIONSHIP_CODES_ALL.CARDHOLDER
    )[0];
  }

  resetScroll(): void {
    if (this.addDependentContainer) {
      this.addDependentContainer.nativeElement.scrollTop = 0;
    }
  }

  public addIndependent(): void {
    this.isSaving = true;
    const reqObj: MemberDetails = {
      clientId: this.memberDetails.clientId,
      cardId: this.memberDetails.cardId,
      firstName: this.personalIfoFormGroup.value.firstName.trim(),
      lastName: this.personalIfoFormGroup.value.lastName.trim(),
      middleName: this.personalIfoFormGroup.value.middleName?.trim(),
      DOB: formatDate(new Date(this.personalIfoFormGroup.value.dob)),
      gender: this.personalIfoFormGroup.value.gender,
      clientInternalMemberId:
        this.personalIfoFormGroup.value.clientInternalMemberId,
      SSN: clearSsnFormat(this.personalIfoFormGroup.value.ssn),
      email: this.contactInfoFormGroup.value.email,
      addressLine1: this.memberDetails.addressLine1,
      addressLine2: this.memberDetails.addressLine2,
      zipCode: this.memberDetails.zipCode,
      city: this.memberDetails.city,
      state: this.memberDetails.state,
      effectiveDate: formatDate(
        new Date(this.personalIfoFormGroup.value.effectiveDate)
      ),
      terminationDate: formatDate(new Date(this.memberDetails.coverage.endDate)),
      isUpdated: false,
      groupNumber: this.memberDetails.coverage?.groupNumber,
      coverageType: this.personalIfoFormGroup.value.coverageType,
      relationshipCode: this.personalIfoFormGroup.value.relationShip,
    };
    if (this.contactInfoFormGroup.value.phoneType !== '') {
      reqObj[this.contactInfoFormGroup.value.phoneType] =
        this.contactInfoFormGroup.value.phoneNumber;
    }
    reqObj.isCardHolder = false;
    this.appInsightService.trackPageView(AppCenterAnalytics.ADD_DEPENDENT_BUTTON_CLICKED, { data: reqObj });
    this.loader.showLoader();
    this.loader.showWaitingMessage$.next(
      AppConstants.ERROR.WAIT_MESSAGE_FOR_UPDATE_MEMBER
    );
    this.eligibilityService.addMember(reqObj).subscribe({
      next: (res) => {
        if (res) {
          this.newDependent = res;
          this.loader.showWaitingMessage$.next(null);
          this.loader.hideLoader();
          this.isSaving = false;
          if (this.cardHolderFutureCoverage && new Date(this.memberDetails?.coverage?.startDate).valueOf() <= new Date().valueOf()) {
            const dialogRef: MatDialogRef<ApplyCardholderFutureCoverageDialog> =
              this.matDialog.open(ApplyCardholderFutureCoverageDialog, {
                autoFocus: false,
                maxWidth: window.innerWidth < 766 ? 350 : 800,
                minWidth: window.innerWidth < 766 ? 350 : 700,
                data: { dependentDetails: res, cardholderDetails: this.memberDetails, cardHolderFutureCoverage: this.cardHolderFutureCoverage },
              });
            dialogRef.afterClosed().subscribe(() => {
              this.addDependentSuccessHandler(res);
            });
          } else {
            this.addDependentSuccessHandler(res);
          }
        }
      },
      error: async (err) => {
        let familyDetails;
        let memberDetails;
        this.createAuditLog(false, err, reqObj);
        if (
          err?.error?.Message?.toLowerCase().trim() ===
          'Existing Member Found'.toLowerCase().trim()
        ) {
          const details = {
            clientId: err.error.Data?.clientId,
            cardId: err.error.Data?.cardId,
          };
          const query = this.eligibilityService.getSearchQuery(details);
          await this.eligibilityService
            .getMemberDetails(query)
            .pipe(
              tap((res) => {
                familyDetails = res;
                memberDetails = this.filterOutMemberDetails(
                  familyDetails,
                  err.error.Data?.personCode
                );
              })
            )
            .toPromise();
          const dialogRef: MatDialogRef<MemberModificationErrorHandlerComponent> =
            this.matDialog.open(MemberModificationErrorHandlerComponent, {
              autoFocus: false,
              panelClass: 'member-already-exists-dialog-panel',
              data: {
                title: 'Dependent Already Exists',
                message:
                  'The dependent you are trying to add is already part of this family.',
                familyDetails: familyDetails,
                memberDetails: memberDetails,
              },
            });
          this.loader.showWaitingMessage$.next(null);
          this.loader.hideLoader();
          this.isSaving = false;
        } else {
          const message = getErrorMessage(err);
          this.errorManager.throwError({
            type: ErrorType.ERROR_GENERIC,
            message: message,
            component: 'Add Dependent',
            title: '',
          });
          this.loader.showWaitingMessage$.next(null);
          this.loader.hideLoader();
          this.isSaving = false;
        }
      },
      complete: () => {
        this.createAuditLog(true);
      },
    });
  }

  private addDependentSuccessHandler(res: MemberDetails) {
    res.PhoneType = this.contactInfoFormGroup.value.phoneType;
    res.firstName = this.personalIfoFormGroup.value.firstName;
    res.dateOfBirth = formatDate(
      new Date(this.personalIfoFormGroup.value.dob)
    );
    res.lastName = this.personalIfoFormGroup.value.lastName;
    res.coverageType = this.personalIfoFormGroup.value.coverageType;
    res[res.PhoneType] = this.contactInfoFormGroup.value.phoneNumber;
    res.gender = this.personalIfoFormGroup.value.gender;
    res.startDate = formatDate(
      new Date(this.personalIfoFormGroup.value.effectiveDate)
    );
    res.patientNum = this.memberDetails.cardId;
    res.email = this.contactInfoFormGroup.value.email;
    (res.addressLine1 = this.memberDetails.addressLine1),
      (res.addressLine2 = this.memberDetails.addressLine2),
      (res.zipCode = this.memberDetails.zipCode);
    res.city = this.memberDetails.city;
    res.state = this.memberDetails.state;
    const dialogRef: MatDialogRef<AddMemberResponseDialogComponent> = this.matDialog.open(AddMemberResponseDialogComponent, {
      autoFocus: false,
      maxWidth: window.innerWidth < 766 ? 350 : 800,
      minWidth: window.innerWidth < 766 ? 350 : 700,
      data: { member: res, cardholderDetails: this.memberDetails },
    });

    dialogRef.afterClosed().subscribe((autoClosed) => {
      if (!autoClosed) {
        this.navigateBackToMemberSearch();
      }
    });
  }

  createAuditLog(
    isSuccess: boolean,
    err?: HttpErrorResponse,
    memberDetails?: MemberDetails
  ) {
    if (memberDetails) {
      this.newDependent = memberDetails;
      this.newDependent.dateOfBirth = memberDetails.DOB.toString();
    }
    const reqBody: SaveAuditLogsRequestInterface = {
      clientId: this.memberDetails.clientId,
      cardId: this.memberDetails.cardId,
      personCd: this.newDependent.personCode,
      firstName: this.newDependent.firstName,
      lastName: this.newDependent.lastName,
      empirxMemberId: this.newDependent.empirxMemberId ?? null,
      emailId:
        this.newDependent.email &&
          this.newDependent.email.toLowerCase() !== 'null'
          ? this.newDependent.email
          : this.authService.userEmail,
      birthDt: this.newDependent.dateOfBirth,
      groupNo: this.newDependent.groupNum,
      groupName: this.groupInfo.groupName,
      changes:
        AppConstants.AUDIT_LOGS.addDependent.action.addNewDependent.change,
      comments:
        AppConstants.AUDIT_LOGS.addDependent.action.addNewDependent.comments,
      addedBy: this.authService.userEmail,
      isSuccess: isSuccess,
    };
    if (!isSuccess) {
      const message = err?.error?.Message
        ? err?.error?.Message
        : JSON.stringify(err);
      (reqBody.httpStatusCode = err.status), (reqBody.errorMsg = message);
    }
    // console.log('Request Body create audit log:', reqBody);
    this.auditLogsService.saveAuditLog(reqBody).subscribe({
      next: (res) => {
        this.appInsightService.trackPageView(
          AppCenterAnalytics.SAVE_AUDIT_LOG_API_SUCCESS,
          { Response: res }
        );
      },
      error: (err) => {
        this.appInsightService.trackPageView(
          AppCenterAnalytics.SAVE_AUDIT_LOG_API_ERROR,
          { Response: err }
        );
        // this.loader.hideLoader();
      },
      complete: () => {
        //   this.loader.hideLoader();
      },
    });
  }

  public cancel(): void {
    this.router.navigateByUrl('/management/online-eligibility');
  }

  public navigateBackToMemberSearch(): void {
    this.eligibilityService.searchCriteriaCache = {
      client: this.memberDetails.clientId,
      idType: 'Card/Alt ID',
      idValue: this.memberDetails.cardId,
    };
    this.eligibilityService.selectedIndex = 0;
    const searchQuery: MemberSearchRequest = {
      clientId: this.memberDetails.clientId,
    };
    (searchQuery.cardId = this.memberDetails.cardId),
      (searchQuery.limit = 10),
      (searchQuery.offset = '0'),
      (searchQuery.count = 1);
    this.eligibilityService.queryString.next(
      this.eligibilityService.getSearchQuery(searchQuery)
    );
    this.router.navigateByUrl('/management/online-eligibility');
  }

  public checkIfCoverageExpired(element): boolean {
    return (
      new Date(element.coverage.endDate).valueOf() <
      new Date(new Date().setUTCHours(0, 0, 0, 0)).valueOf()
    ) && !element.isSoftDeleted;
  }

  public checkIfCoverageIsStartingSoon(element): boolean {
    return (
      new Date(element.coverage.startDate).valueOf() >
      new Date(new Date().setUTCHours(0, 0, 0, 0)).valueOf()
    ) && !element.isSoftDeleted;
  }

  public checkIfCoverageSoftDeleted(element): boolean {
    return element.isSoftDeleted;
  }

  public getDependents(): Observable<MemberDetails[]> {
    return defer(() => {
      return of(this.familyDetails).pipe(
        map((res) => {
          this.checkIfSpouseExists(res);
          return res.filter(
            (i) =>
              i.relationshipCode !==
              AppConstants.RELATIONSHIP_CODES_ALL.CARDHOLDER
          );
        })
      );
    });
  }

  public toggle() {
    this.isExpanded = !this.isExpanded;
  }

  private checkIfSpouseExists(res: MemberDetails[]) {
    const SPOUSE_CODE = AppConstants.RELATIONSHIP_CODES_ALL.SPOUSE.trim();

    const spouseMembers = res.filter(
      (m) => m.relationshipCode && m.relationshipCode.trim() === SPOUSE_CODE
    );
    this.appInsightService.trackPageView(
      AppCenterAnalytics.ADD_DEPENDENT_FILTERED_SPOUSE_FROM_FAMILY,
      { data: spouseMembers }
    );
    console.log('Filtered spouse members:', spouseMembers);

    if (spouseMembers.length > 0) {
      const index = this.relationshipData.findIndex(
        (d) => d.value && d.value.trim() === SPOUSE_CODE
      );
      if (index !== -1) {
        this.appInsightService.trackPageView(
          AppCenterAnalytics.ADD_DEPENDENT_REMOVED_SPOUSE_FROM_FAMILY,
          { data: index }
        );
        this.relationshipData.splice(index, 1);
      }
    }
  }
}
